import type { AuditLog } from "@aidkitorg/aidkit/lib/admin_panel/audit_logs/repository/IAuditLogRepository";

// One place a document lives, or used to live: an applicantinfo row whose value contained
// the document's URL, or a comms attachment.
export interface FileHubDocument {
  url: string;
  created_at: string;
  source: string;
  target_field: string | null;
  latest: boolean | null;
}

export type HistoryEntry = {
  // The 10 second bucket this entry was grouped into. Doubles as a stable React key.
  bucket: number;
  timestamp: number;
  note?: string;
  action?: 'copy_to' | 'remove' | 'move_to';
  targetField?: string;
  sourceField?: string;
  renamedTo?: string;
  source: string;
}

export function isAuditLog(entry: AuditLog | FileHubDocument): entry is AuditLog {
  return 'event' in entry;
}

function getEntryTimestamp(entry: AuditLog | FileHubDocument): number {
  const raw = isAuditLog(entry) ? entry.timestamp : entry.created_at;
  return new Date(raw).getTime();
}

/**
 * Collapses everything we know about one file - every applicantinfo row its URL has ever
 * appeared in, plus its move/rename audit log events - into the changes that actually
 * happened to *that file*, newest first.
 */
export function buildFileHistory(entries: (AuditLog | FileHubDocument)[]): HistoryEntry[] {
  // Bucket the events by timestamp - we prefer audit log events over target field movements, but
  // we are not always guaranteed to have an audit log event for each movement of the file.
  const buckets = entries.reduce<Record<number, HistoryEntry>>((acc, entry) => {
    // bucket by 10 second intervals - this should guarantee that a TF movement that happened with an Audit Log event will be grouped together,
    // but nothing will be grouped erroneously.
    const timestamp = getEntryTimestamp(entry);
    const bucket = Math.round(timestamp / 10_000);
    acc[bucket] ??= { bucket, timestamp, source: '' };
    acc[bucket].timestamp = Math.max(acc[bucket].timestamp, timestamp);
    if (isAuditLog(entry)) {
      // Only assign fields that are present so a rename event (which carries
      // `name` but no action/target_field) doesn't clobber a move that landed
      // in the same time bucket, and vice versa.
      if (entry.value?.note) acc[bucket].note = entry.value.note;
      if (entry.value?.action) acc[bucket].action = entry.value.action;
      if (entry.value?.target_field) acc[bucket].targetField = entry.value.target_field;
      if (entry.value?.source_field) acc[bucket].sourceField = entry.value.source_field;
      if (entry.value?.name) acc[bucket].renamedTo = entry.value.name;
      acc[bucket].source = entry.userName;
    } else {
      if (!acc[bucket].targetField) acc[bucket].targetField = entry.target_field ?? undefined;
      if (!acc[bucket].source) acc[bucket].source = entry.source;
    }
    return acc;
  }, {});

  const sortedBuckets = Object.keys(buckets).map(Number).sort((a, b) => a - b);

  // Replay oldest to newest, tracking which fields the file currently lives in. A row that is not an
  // audit log event, for a field the file is already in, is just a rewrite of that field's value
  // (e.g. another document was copied or moved into it) and did not touch this file, so drop it.
  // This has to be the file's full set of fields rather than the immediately older entry: a rename
  // or a copy to some other field can sit between the file's arrival and the unrelated rewrite.
  // Comms attachments have no field and share the '' key, so repeats of one attachment collapse too.
  const fieldsContainingFile = new Set<string>();
  const history: HistoryEntry[] = [];
  for (const bucket of sortedBuckets) {
    const entry = buckets[bucket];
    const field = entry.targetField ?? '';

    if (entry.action === 'remove') {
      fieldsContainingFile.delete(field);
    } else if (entry.action) {
      if (entry.action === 'move_to' && entry.sourceField) fieldsContainingFile.delete(entry.sourceField);
      fieldsContainingFile.add(field);
    } else if (!entry.renamedTo) {
      if (fieldsContainingFile.has(field)) continue;
      fieldsContainingFile.add(field);
    } else if (entry.targetField) {
      // A rename that landed in the same bucket as a field write still records that write.
      fieldsContainingFile.add(field);
    }

    history.push(entry);
  }

  return history.reverse();
}

/**
 * The last change that was really made to each file, keyed by URL.
 *
 * The File Hub can't take this from the applicantinfo row a document currently sits on:
 * that row records whoever last wrote the *field*, which is the wrong person (and the wrong
 * time) whenever that write was about some other document on the same field.
 */
export function lastModifiedByFile(history: Record<string, (AuditLog | FileHubDocument)[]>) {
  const lastModified: Record<string, { source: string, timestamp: number }> = {};
  for (const [url, entries] of Object.entries(history)) {
    const [latest] = buildFileHistory(entries);
    if (latest) lastModified[url] = { source: latest.source, timestamp: latest.timestamp };
  }
  return lastModified;
}
